import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import DeleteModal from '../../../components/DeleteModal';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import MyAppText from '../../../components/MyAppText';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import getBestContrastColor from '../../../services/best-contrast-color';
import Badge, { BadgeColor } from '../../../components/Badge';
import { accessService } from '../../../services/central-api/access';
import { ClientError } from '../../../services/central-api/base-service';
import { translate } from '../../../services/translate';
import { AccessParamList } from '../../../typings/Params';
import UsersFilter from './Filters';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import BlockUserModal from './BlockUserModal';
import UnblockModal from './UnblockModal';
import moment from 'moment';

export default function Users() {
    const navigation = useNavigation<NavigationProp<AccessParamList>>();
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const route = useRoute<RouteProp<AccessParamList, 'Users'>>();

    const [users, setUsers] = useState<UserData[]>([]);

    const [selectedUser, setSelectedUser] = useState<UserData>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const [selectedUserToBlock, setSelectedUserToBlock] = useState<UserData>();
    const [showBlockUserModal, setShowBlockUserModal] = useState<boolean>(false);

    const [selectedUserToUnblock, setSelectedUserToUnblock] = useState<UserData>();
    const [isUnblockModalVisible, setUnblockModalVisible] = useState(false);

    const [filters, setFilters] = useFilters<AccessParamList['Users']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessUsers', filters],
        queryFn: () => {
            return accessService.getUsers(filters);
        },
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setUsers(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(user: UserData) {
        try {
            if (user?.id) {
                await accessService.deleteUser(user.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });

                setDeleteModalVisible(false);
                refetch();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function confirmUnblock(user: UserData) {
        try {
            if (user?.id) {
                await accessService.unblockUser(user.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });

                setUnblockModalVisible(false);
                refetch();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function canShowDeleteButton(row: UserData) {
        if (authenticatedUser?.id === row.id) {
            return false;
        }

        if (!authenticatedUser?.isAdmin && row.isAdmin) {
            return false;
        }

        if (authenticatedUser?.permissions.access_remove_user_acting_body && row.actingBodyUnit?.actingBodyId === authenticatedUser?.actingBodyUnit?.actingBodyId) {
            return true;
        }

        if (authenticatedUser?.permissions.access_remove_user_acting_body_unit && row.actingBodyUnitId === authenticatedUser?.actingBodyUnitId) {
            return true;
        }

        if (authenticatedUser?.permissions.access_remove_user_all_acting_bodies) {
            return true;
        }

        return !!authenticatedUser?.isAdmin;
    }

    function canShowEditButton(row: UserData) {
        if (!authenticatedUser?.isAdmin && row.isAdmin) {
            return false;
        }

        if (authenticatedUser?.permissions.access_edit_user_all_acting_bodies
            || authenticatedUser?.permissions.access_edit_user_acting_body_units
            || authenticatedUser?.permissions.access_edit_user_settings
        ) {
            return true;
        }

        return !!authenticatedUser?.isAdmin;
    }

    function canShowBlockButton(row: UserData) {
        if (authenticatedUser?.id === row.id) {
            return false;
        }

        if (!authenticatedUser?.isAdmin && row.isAdmin) {
            return false;
        }

        if (row.blockDate) {
            return false;
        }

        if (getVisitorExpiredStatus(row)) {
            return false;
        }

        return (authenticatedUser?.permissions.access_edit_user_blocked_all_acting_bodies
            || authenticatedUser?.permissions.access_edit_user_blocked_acting_body
            || authenticatedUser?.permissions.access_edit_user_blocked_acting_body_units);
    }

    function canShowUnblockButton(row: UserData) {
        if (authenticatedUser?.id === row.id) {
            return false;
        }

        if (!authenticatedUser?.isAdmin && row.isAdmin) {
            return false;
        }

        if (!row.blockDate && !getVisitorExpiredStatus(row)) {
            return false;
        }

        return !!(authenticatedUser?.permissions.access_edit_user_blocked_all_acting_bodies
            || authenticatedUser?.permissions.access_edit_user_blocked_acting_body
            || authenticatedUser?.permissions.access_edit_user_blocked_acting_body_units);
    }

    function getBlockNatureColor(nature: string): BadgeColor {
        switch (nature) {
            case 'manual':
                return 'teal';
            case 'inactivity':
                return 'yellow';
            case 'exceeded_login_attempts':
                return 'red';
            case 'expiration':
                return 'purple';
            default:
                return 'red';
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    function getVisitorExpiredStatus(row: UserData): boolean {
        if (!row.visitorStartDate && !row.visitorEndDate) {
            return false;
        }

        const now = moment();
        const start = row.visitorStartDate ? moment(row.visitorStartDate).startOf('day') : undefined;
        const end = row.visitorEndDate ? moment(row.visitorEndDate).endOf('day') : undefined;

        if (start && now.isBefore(start)) {
            return true;
        }
        if (end && now.isAfter(end)) {
            return true;
        }

        return false;
    }

    return (
        <Container>
            <Header
                title={translate('users')}
                actions={(authenticatedUser?.permissions.access_edit_user_all_acting_bodies
                    || authenticatedUser?.permissions.access_edit_user_acting_body_units
                    || authenticatedUser?.permissions.access_edit_user_settings) &&
                    <CustomButton text={translate('register')} onPress={() => {
                        setSelectedUser(undefined);
                        navigation.navigate('UserForm');
                    }} />
                }
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredUsers')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={text => {
                            setTextFilter(text);
                        }}
                        placeholder={translate('userFilterPlaceholder')}
                        onKeyPress={handleKeyDown}
                    />}
                    filter={<UsersFilter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} authenticatedUser={authenticatedUser} />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[{
                            name: translate('status'),
                            cell: row => (
                                <View style={{ justifyContent: 'center' }}>
                                    {row.blockDate ? (
                                        <Badge mode='SQUARE' size='medium' color={getBlockNatureColor(row.userBlockNature)}>
                                            {translate(row.userBlockNature)}
                                        </Badge>
                                    ) : getVisitorExpiredStatus(row) ? (
                                        <Badge mode='SQUARE' size='medium' color={getBlockNatureColor('expiration')}>
                                            {translate('expiration')}
                                        </Badge>
                                    ) : (
                                        <Badge mode='SQUARE' size='medium' color='green'>
                                            {translate('active')}
                                        </Badge>
                                    )}
                                </View>
                            ),
                            wrap: true,
                            minWidth: '150px'
                        },
                        {
                            name: translate('registry'),
                            selector: row => row.registry,
                            wrap: true,
                        },
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            wrap: true,
                            grow: 3
                        },
                        {
                            name: translate('email'),
                            selector: row => row.email,
                            wrap: true,
                            grow: 2
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.role?.actingBody?.name || '',
                            wrap: true,
                        },
                        {
                            name: translate('unit'),
                            selector: row => row.actingBodyUnit?.name || '',
                            wrap: true,
                        },
                        {
                            name: translate('role'),
                            selector: row => row.role?.name || '',
                            wrap: true,
                        },
                        {
                            name: translate('tags'),
                            cell: row =>
                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5, paddingVertical: 5 }}>
                                    {row.tags ?
                                        row.tags.map((tag) =>
                                            <View key={tag.id} style={[styles.marker, { backgroundColor: tag.color }]}>
                                                <MyAppText style={styles.markerText(tag.color)}>
                                                    {tag.name}
                                                </MyAppText>
                                            </View>
                                        ) : null
                                    }
                                </View>
                        },
                        {
                            button: true,
                            cell: row => <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedUser(row);
                                        navigation.navigate('UserForm', { id: row.id });
                                    },
                                    omit: !canShowEditButton(row),
                                }, {
                                    content: translate('blockUser'),
                                    variant: 'warning',
                                    onPress: () => {
                                        setSelectedUserToBlock(row);
                                        setShowBlockUserModal(true);
                                    },
                                    omit: !canShowBlockButton(row),
                                }, {
                                    content: translate('unblockUser'),
                                    onPress: () => {
                                        if (row.userBlockNature === 'manual') {
                                            setSelectedUserToUnblock(row);
                                            setUnblockModalVisible(true);
                                        } else {
                                            navigation.navigate('UserForm', { id: row.id });
                                        }
                                    },
                                    omit: !canShowUnblockButton(row),
                                }, {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedUser(row);
                                        setDeleteModalVisible(true);
                                    },
                                    omit: !canShowDeleteButton(row),
                                }]}
                            />
                        }]}
                        data={users}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            {selectedUserToBlock && <BlockUserModal
                isModalVisible={showBlockUserModal}
                setModalVisible={setShowBlockUserModal}
                userId={selectedUserToBlock.id}
                onConfirm={refetch}
            />}
            <UnblockModal
                setModalVisible={setUnblockModalVisible}
                isModalVisible={isUnblockModalVisible}
                itemName={selectedUserToUnblock?.name}
                onSubmit={async () => {
                    if (selectedUserToUnblock) {
                        await confirmUnblock(selectedUserToUnblock);
                    }
                }}
            />
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={String(selectedUser?.name) || ''}
                onSubmit={async () => {
                    if (selectedUser) {
                        await confirmDelete(selectedUser);
                    }
                }}
            />
        </Container>
    );
}

const styleSheet = createStyleSheet(() => ({
    marker: {
        borderRadius: 8,
        minHeight: 16,
        paddingLeft: 10,
        paddingRight: 10,
        maxWidth: '100%'
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
}));
